import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeftIcon, ArrowPathIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getBooklet, getBookletCheck } from "../../services/bookletService";
import { getApiErrorCode } from "../../utils/apiError";
import { formatDateTime } from "../../utils/formatDate";
import { HelpTitle } from "../../components/Help/HelpButton";
import BtsGuide from "../../components/Help/guides/BtsGuide";
import { cardClass, focusRing, iconButtonClass, Spinner } from "../Exams/TestBuilder/ui";
import { backLinkClass, BookletStatusBadge, bookletErrorMessage, bookletMeta, Codes, FrozenBadge, listHref } from "./bts";
import BookletCheck from "./BookletCheck";
import BookletLabels from "./BookletLabels";
import BookletSections from "./BookletSections";
import BookletSettings, { ACTIVE_FIELDS } from "./BookletSettings";
import BookletStatusActions from "./BookletStatusActions";
import BookletPrint from "./BookletPrint";
import BookletKeys from "./BookletKeys";
import BookletSteps, { STEP_ANCHORS } from "./BookletSteps";

const NO_UNSAVED = { sections: false, settings: [], labels: false };
const differs = (a, b) => JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);

// How the subjects changed between two reads: "added: …; removed: …; order changed" (or "").
function sectionChanges(t, before, after) {
  const subjectOf = (s) => s.variant?.folder?.uuid || s.variant?.uuid;
  const nameOf = (s) => s.variant?.folder?.name || s.variant?.name || "";
  const was = new Map(before.map((s) => [subjectOf(s), s]));
  const is = new Map(after.map((s) => [subjectOf(s), s]));
  const added = after.filter((s) => !was.has(subjectOf(s))).map(nameOf);
  const removed = before.filter((s) => !is.has(subjectOf(s))).map(nameOf);
  const replaced = after.filter((s) => was.has(subjectOf(s)) && was.get(subjectOf(s)).variant?.uuid !== s.variant?.uuid).map(nameOf);
  const moved = differs(before.map(subjectOf).filter((key) => is.has(key)), after.map(subjectOf).filter((key) => was.has(key)));
  return [
    added.length && t("bts.changes.sections_added", { count: added.length, names: added.join(", ") }),
    removed.length && t("bts.changes.sections_removed", { count: removed.length, names: removed.join(", ") }),
    replaced.length && t("bts.changes.sections_replaced", { count: replaced.length, names: replaced.join(", ") }),
    moved && t("bts.changes.sections_moved"),
  ]
    .filter(Boolean)
    .join("; ");
}

// What someone else changed, of what the page shows ("name", "subjects (removed: History)"…).
function bookletChanges(t, before, after) {
  if (!before || !after) return [];
  const sorted = (digits) => [...(digits || [])].sort((a, b) => a - b);
  const sections = sectionChanges(t, before.check?.sections || [], after.check?.sections || []);
  return [
    differs((before.name || "").trim(), (after.name || "").trim()) && t("bts.changes.name"),
    differs((before.description || "").trim(), (after.description || "").trim()) && t("bts.changes.description"),
    differs(before.folder?.uuid, after.folder?.uuid) && t("bts.changes.folder"),
    differs(before.grade, after.grade) && t("bts.changes.grade"),
    differs(before.language, after.language) && t("bts.changes.language"),
    differs(before.round_number, after.round_number) && t("bts.changes.round_number"),
    differs(sorted(before.variant_numbers), sorted(after.variant_numbers)) && t("bts.changes.variant_numbers"),
    differs(before.duration_minutes, after.duration_minutes) && t("bts.changes.duration"),
    differs(before.labels, after.labels) && t("bts.changes.labels"),
    sections && t("bts.changes.sections", { details: sections }),
    before.status !== after.status && t("bts.changes.status", { status: t(`bts.status.${after.status}`, { defaultValue: after.status }) }),
  ].filter(Boolean);
}

// Admin: a BTS booklet (/bts/:uuid). Each booklet gets a page of its own: the answers still on their way for
// the booklet left (back and forth between booklets, a link to another one) change nothing on the next one.
export default function BtsDetail() {
  const { uuid } = useParams();
  return <BookletDetail key={uuid} uuid={uuid} />;
}

// The booklet's status, check, subjects, settings, printed phrases, PDFs and keys.
function BookletDetail({ uuid }) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const location = useLocation();
  const back = listHref(location.state);
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sourcesKey, setSourcesKey] = useState(0);
  const [checkKey, setCheckKey] = useState(0);
  const [optionsChanged, setOptionsChanged] = useState(null);
  // What someone else changed meanwhile (after a 409 and a new read).
  const [changes, setChanges] = useState(null);
  const [unsaved, setUnsaved] = useState(NO_UNSAVED);
  // Only the latest read counts: an older answer arriving late changes nothing.
  const genRef = useRef(0);
  const checkGenRef = useRef(0);
  const refreshCallRef = useRef(0);
  const detailRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  // The booklet read: its detail once shown; false when the read failed; null when a newer read or write came
  // first (or the page is gone).
  const load = useCallback(async () => {
    const gen = ++genRef.current;
    try {
      const data = await getBooklet(uuid);
      if (gen !== genRef.current || !mountedRef.current) return null;
      setDetail(data);
      setLoadError(null);
      return data;
    } catch (err) {
      if (gen !== genRef.current || !mountedRef.current) return null;
      setLoadError(err);
      return false;
    }
  }, [uuid]);

  useEffect(() => {
    load();
  }, [load]);

  // A write's answer is the booklet as it is now: a read still on its way is outdated, and what is shown is
  // current again.
  const applyDetail = useCallback((data) => {
    genRef.current += 1;
    checkGenRef.current += 1;
    setDetail(data);
    setLoadError(null);
    setChanges(null);
  }, []);
  // A fresh check (the activation dialog's): the codes other booklets take are read again too.
  const applyCheck = useCallback((check) => {
    checkGenRef.current += 1;
    setDetail((prev) => (prev ? { ...prev, check, frozen: check.frozen ?? prev.frozen } : prev));
    setCheckKey((key) => key + 1);
  }, []);

  const refreshCheck = useCallback(async () => {
    const gen = ++checkGenRef.current;
    const call = ++refreshCallRef.current;
    setRefreshing(true);
    try {
      const check = await getBookletCheck(uuid);
      if (gen === checkGenRef.current) setDetail((prev) => (prev ? { ...prev, check, frozen: check.frozen ?? prev.frozen } : prev));
    } catch (err) {
      if (gen === checkGenRef.current) toast.error(bookletErrorMessage(t, err, "bts.check.refresh_failed"));
    } finally {
      // A write meanwhile makes this check outdated, not the button busy.
      if (call === refreshCallRef.current) setRefreshing(false);
    }
    setSourcesKey((key) => key + 1);
  }, [uuid, t]);

  // Back on the tab (a variant confirmed in the bank meanwhile, say): the check and the subjects are read again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && detail) refreshCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshCheck, detail]);

  // Someone changed the booklet meanwhile: read it again and say what changed; what the user typed stays in the
  // forms. Until a read succeeds, a save would send the old revision and be refused again.
  const onStale = useCallback(
    async (err) => {
      const code = getApiErrorCode(err);
      if (code !== "booklet_changed") toast.error(bookletErrorMessage(t, err, "bts.changed_reloaded"));
      const before = detailRef.current;
      const fresh = await load();
      if (!mountedRef.current) return;
      if (fresh === false) {
        if (code === "booklet_changed") toast.error(t("bts.changed_reload_failed"));
        return;
      }
      if (code === "booklet_changed") toast.warning(t("bts.changed_reloaded"));
      if (fresh) {
        const parts = bookletChanges(t, before, fresh);
        setChanges(parts.length ? parts : null);
      }
    },
    [load, t]
  );

  const onStatusChanged = useCallback(
    (data) => {
      applyDetail(data);
      setOptionsChanged(Array.isArray(data.options_changed) && data.options_changed.length ? data.options_changed : null);
    },
    [applyDetail]
  );

  // The cards say whether they hold unsaved changes (the settings: which fields).
  const onDirtyChange = useCallback((card, value) => {
    setUnsaved((prev) => (differs(prev[card], value) ? { ...prev, [card]: value } : prev));
  }, []);
  const anyUnsaved = unsaved.sections || unsaved.labels || unsaved.settings.length > 0;
  // The cards whose unsaved changes an activation would leave out: it freezes the booklet as saved (the name and
  // the description stay editable).
  const activationUnsaved = [
    unsaved.sections && t("bts.sections.title"),
    unsaved.settings.some((field) => !ACTIVE_FIELDS.includes(field)) && t("bts.settings.title"),
    unsaved.labels && t("bts.labels.title"),
  ].filter(Boolean);

  // Closing or reloading the tab with unsaved changes asks first.
  useEffect(() => {
    if (!anyUnsaved) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyUnsaved]);

  // So does a link of the page that leaves it (the way back, another booklet); the bank's links open a new tab.
  const guardLeave = (event) => {
    if (!anyUnsaved || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!link || link.target === "_blank") return;
    if (!window.confirm(t("bts.leave_confirm"))) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (loadError && !detail) {
    const missing = loadError?.response?.status === 404;
    return (
      <div className="flex min-w-0 flex-col gap-5">
        <Link to={back} className={backLinkClass}>
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t("nav.bts")}
        </Link>
        <div className={`${cardClass} flex flex-col items-center gap-3 p-10 text-center`} role="alert">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-500" aria-hidden="true" />
          <p className="text-sm font-medium text-slate-800">{missing ? t("bts.detail.not_found") : t("bts.detail.load_error")}</p>
          {!missing && (
            <button
              type="button"
              onClick={load}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 ${focusRing}`}
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t("bts.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className={`${cardClass} flex items-center justify-center gap-3 p-10`} role="status">
        <Spinner className="h-5 w-5" />
        <span className="text-sm text-slate-500">{t("bts.detail.loading")}</span>
      </div>
    );
  }

  const meta = [
    bookletMeta(t, detail),
    detail.duration_minutes ? t("bts.detail.duration", { count: detail.duration_minutes }) : "",
  ].filter(Boolean);
  // The codes other active booklets take are read again with the check, a status change and every write.
  const takenKey = `${sourcesKey}:${checkKey}:${detail.status}:${detail.revision}`;

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in" onClickCapture={guardLeave}>
      <Link to={back} className={backLinkClass}>
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        {t("nav.bts")}
      </Link>

      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {/* The guide's "?" right after the booklet's name, before its status. */}
            <HelpTitle helpId="bts" helpTitle={t("help.bts.title")} guide={<BtsGuide />} className="break-words">
              {detail.name || t("bts.untitled")}
            </HelpTitle>
            <BookletStatusBadge status={detail.status} />
            {detail.frozen && <FrozenBadge />}
          </div>
          <p className="mt-1 text-sm text-slate-500">{meta.join(" · ")}</p>
          {detail.description?.trim() && <p className="mt-1 max-w-3xl whitespace-pre-line break-words text-sm text-slate-600">{detail.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
            <span>{t("bts.detail.codes", { count: detail.codes?.length || 0 })}</span>
            <Codes codes={detail.codes} />
          </div>
          <p className="mt-1 text-xs text-slate-500">{t("bts.detail.updated", { date: formatDateTime(detail.updated, language) })}</p>
        </div>
        <BookletStatusActions
          booklet={detail}
          unsaved={activationUnsaved}
          onChanged={onStatusChanged}
          onStale={onStale}
          onCheck={applyCheck}
          onReload={load}
        />
      </header>

      {loadError && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{t("bts.detail.reload_error")}</span>
          <button
            type="button"
            onClick={load}
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-200 transition duration-200 hover:bg-red-100 max-sm:h-11 ${focusRing}`}
          >
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
            {t("bts.retry")}
          </button>
        </div>
      )}

      {changes && (
        <div role="status" className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="min-w-0 flex-1 break-words">{t("bts.changes.banner", { changes: changes.join(", ") })}</p>
          <button
            type="button"
            onClick={() => setChanges(null)}
            aria-label={t("buttons.close")}
            title={t("buttons.close")}
            className={`${iconButtonClass} -my-1 max-sm:h-11 max-sm:w-11`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {optionsChanged && (
        <div role="status" className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1">{t("bts.detail.options_changed", { count: optionsChanged.length })}</p>
          <button
            type="button"
            onClick={() => setOptionsChanged(null)}
            aria-label={t("buttons.close")}
            title={t("buttons.close")}
            className={`${iconButtonClass} -my-1 max-sm:h-11 max-sm:w-11`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* The way a booklet is made, where this one stands and what to do now; the cards follow that order. */}
      <BookletSteps booklet={detail} />

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_26rem] 2xl:grid-cols-[minmax(0,1fr)_30rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <div id={STEP_ANCHORS.subjects} className="scroll-mt-4">
            <BookletSections booklet={detail} onSaved={applyDetail} onStale={onStale} sourcesKey={sourcesKey} onDirtyChange={onDirtyChange} />
          </div>
          <div id={STEP_ANCHORS.check} className="scroll-mt-4">
            <BookletCheck booklet={detail} onRefresh={refreshCheck} refreshing={refreshing} />
          </div>
          <div id={STEP_ANCHORS.print} className="flex scroll-mt-4 flex-col gap-5">
            <BookletPrint booklet={detail} onBookletChange={load} />
            <BookletKeys booklet={detail} />
          </div>
        </div>
        <div id={STEP_ANCHORS.settings} className="flex min-w-0 scroll-mt-4 flex-col gap-5">
          <BookletSettings booklet={detail} onSaved={applyDetail} onStale={onStale} onDirtyChange={onDirtyChange} takenKey={takenKey} />
          <BookletLabels booklet={detail} onSaved={applyDetail} onStale={onStale} onDirtyChange={onDirtyChange} />
        </div>
      </div>
    </div>
  );
}
